import { useContext } from "react";
import { CircleNotch } from "@phosphor-icons/react";
import PracticeHistoryNav from "./PracticeHistoryNav";
import {
  ActivePracticeContext,
  PracticesContext,
} from "../../contexts/contextExporter";
import PracticeTitleContent from "../../components/PracticeTitleContent";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import SelectableGroup from "../../components/formElements/SelectableGroup";
import { IPracticeHistoryItem } from "../../types/generalTypes";
import AiContent from "./AiAnswer";
import { RevealAnswerButton } from "./RevealAnswer";
import { IPracticeTest } from "../../types/practiceTypes";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";

function transformTestAnswer(data: { answer: string | [] }) {
  const dataToSend: { answer: string } = {
    ...data,
    answer:
      typeof data.answer !== "string"
        ? (data.answer as []).sort((a, b) => a - b).join(",")
        : data.answer,
  };
  return dataToSend;
}

export default function TestPractice({
  currentAiContent,
  setAiReply,
  setUserAnswer,
  history,
  isLive,
}: {
  currentAiContent: string | null;
  setAiReply: React.Dispatch<React.SetStateAction<string | null>>;
  setUserAnswer: React.Dispatch<React.SetStateAction<string | null>>;
  history: IPracticeHistoryItem[];
  isLive: boolean;
}) {
  const {
    practice,
    actionStatus,
    setActionStatus,
    sendAnswer,
    isAnswerRevealed,
  } = useContext(ActivePracticeContext);

  const testPractice = practice as IPracticeTest;

  const { nextPractice } = useContext(PracticesContext);
  const methods = useForm();

  const isPastItem = actionStatus.historyIndex > -1;
  const currentHistoryItem = history[actionStatus.historyIndex];

  useKeyboardShortcuts({
    onEnter: !isPastItem ? () => methods.handleSubmit(onSubmit)() : undefined,
    onNext: isPastItem && actionStatus.canGoNext ? nextPractice : undefined,
  });

  const tryAgain = () => {
    setActionStatus((prev) => ({ ...prev, ai: "IDLE", historyIndex: -1 }));
    setAiReply(null);
    methods.reset({ answer: "" });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    setUserAnswer(data.answer);
    sendAnswer(transformTestAnswer(data));
  };

  return (
    <>
      <div className="relative">
        <PracticeHistoryNav
          key={testPractice!.id}
          current={actionStatus.historyIndex}
          total={history.length}
          setCurrent={(newHistoryIndex) =>
            setActionStatus((prev) => ({
              ...prev,
              historyIndex: newHistoryIndex,
            }))
          }
        />

        <div
          className={clsx(
            "bg-surface-1 rounded-2xl p-2 border border-white/[0.08]",
            currentAiContent ? "" : ""
          )}
        >
          {/* form itself */}
          <div
            className={clsx(
              "flex transition-all relative group rounded-2xl",
              isPastItem || actionStatus.ai !== "IDLE"
                ? "text-white/60"
                : "text-white/100"
            )}
          >
            <div className="text-base leading-normal font-medium practiceTitle p-10 grow min-w-72">
              <PracticeTitleContent title={testPractice!.description.title} />
            </div>

            <FormProvider
              {...methods}
              key={`${testPractice!.id}_${actionStatus.historyIndex}`}
            >
              <div className="p-8 pl-0 grow min-w-72">
                <form
                  onSubmit={methods.handleSubmit(onSubmit)}
                  id={`practice_form`}
                  className={
                    actionStatus.ai !== "IDLE" ? "pointer-events-none" : ""
                  }
                >
                  <SelectableGroup
                    answer={testPractice!.description.answer!}
                    options={testPractice!.description.options!}
                    isRevealed={isAnswerRevealed}
                    qId={testPractice!.id}
                    defaultValue={
                      isPastItem ? currentHistoryItem?.user_answer : ""
                    }
                    canReveal={true}
                    isDisabled={isPastItem}
                    type={
                      testPractice!.description.answer!.split(",").length > 1
                        ? "MULTI"
                        : "SINGLE"
                    }
                  />
                </form>
              </div>
            </FormProvider>

            <span className="text-sm text-white absolute bottom-2 right-3 transition-opacity opacity-30 group-hover:opacity-75">
              #{testPractice!.id}
            </span>
          </div>
          {/* form itself, ended */}

          <AiContent
            isFocused={isPastItem || actionStatus.ai !== "IDLE"}
            aiReply={currentAiContent}
            setAiReply={setAiReply}
          />

          {actionStatus.ai === "LOADING" && (
            <div className="rounded-2xl p-8 bg-white/10 text-lg/relaxed text-white/70">
              <span className="flex items-center gap-1 animate-pulse">
                Düşünüyor
                <span className="flex gap-0.5">
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0s" }}
                  >
                    .
                  </span>
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  >
                    .
                  </span>
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0.4s" }}
                  >
                    .
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* gönder, tekrar dene, sonraki soru  */}
      <div className="mt-3 flex gap-2">
        {isPastItem && !actionStatus.canGoNext && !isAnswerRevealed && (
          <div className="practice-button" onClick={tryAgain}>
            Tekrar dene
          </div>
        )}

        {!isPastItem && actionStatus.ai === "LOADING" && (
          <button className="practice-button opacity-60 cursor-wait" disabled>
            <CircleNotch size={16} className="animate-spin inline mr-1.5" />
            Değerlendiriliyor...
          </button>
        )}
        {!isPastItem && actionStatus.ai === "IDLE" && !isAnswerRevealed && (
          <button
            className="practice-button"
            type="submit"
            form={`practice_form`}
          >
            Gönder
          </button>
        )}

        <RevealAnswerButton tryAgain={tryAgain} isLive={isLive} />

        {methods.formState.errors["answer"] && (
          <div className="bg-myRed text-xs py-2 px-3 rounded-full text-white font-semibold self-center">
            {methods.formState.errors["answer"].message as string}
          </div>
        )}

        {isPastItem && actionStatus.canGoNext && (
          <div
            className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen"
            onClick={nextPractice}
          >
            Sonraki Soru
          </div>
        )}
      </div>
    </>
  );
}
